import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../shared/api/errors';
import {
  countWaiting,
  forwardRefusal,
  headerEntries,
  reservedHeaderReason,
  TIMEOUT_SECONDS,
  timeoutSeconds,
  toTimeoutMs,
  updateDestination,
  type TAuthType,
  type TDestination,
  type TDestinationUpdate,
  type THttpMethod,
} from '../../entities/destination';

export interface IHeaderRow {
  key: string;
  value: string;
}

export interface IDestinationDraft {
  name: string;
  url: string;
  method: THttpMethod;
  seconds: string;
  ratePerSec: string;
  rateBurst: string;
  signOutbound: boolean;
  /** `undefined` leaves the stored signing key alone, `''` deletes it. */
  secret?: string;
  authType: TAuthType;
  authUsername: string;
  authHeader: string;
  authPrefix: string;
  /** Same convention as `secret`, for the endpoint credential. */
  authCredential?: string;
  forwardHeaders: string[];
  passThroughPath: boolean;
  passThroughQuery: boolean;
  followRedirects: boolean;
  headers: IHeaderRow[];
  label: string;
  path: string;
  holdTtlSec: string;
  maxHoldEvents: string;
}

export type TDraftField = keyof IDestinationDraft;

const draftOf = (destination: TDestination): IDestinationDraft => {
  const config = destination.config;
  return {
    name: destination.name,
    url: config.url ?? '',
    method: config.method ?? 'POST',
    seconds: timeoutSeconds(destination.timeoutMs),
    ratePerSec: destination.rateLimitPerSec == null ? '' : String(destination.rateLimitPerSec),
    rateBurst: destination.rateLimitBurst == null ? '' : String(destination.rateLimitBurst),
    signOutbound: config.signOutbound === true,
    authType: config.auth?.type ?? 'none',
    authUsername: config.auth?.username ?? '',
    authHeader: config.auth?.header ?? '',
    authPrefix: config.auth?.prefix ?? '',
    forwardHeaders: config.forwardHeaders ?? [],
    passThroughPath: config.passThroughPath === true,
    passThroughQuery: config.passThroughQuery === true,
    followRedirects: config.followRedirects === true,
    headers: headerEntries(config.headers).map(([key, value]) => ({ key, value })),
    label: config.label ?? '',
    path: config.path ?? '',
    holdTtlSec: config.holdTtlSec == null ? '' : String(config.holdTtlSec),
    maxHoldEvents: config.maxHoldEvents == null ? '' : String(config.maxHoldEvents),
  };
};

const whole = (raw: string): number | null => {
  const trimmed = raw.trim();
  return /^\d+$/.test(trimmed) ? Number(trimmed) : null;
};

const optionalWhole = (raw: string, min: number, max: number, unit: string): string | null => {
  if (!raw.trim()) return null;
  const value = whole(raw);
  if (value === null) return `A whole number of ${unit}, or empty`;
  if (value < min || value > max) return `Between ${min} and ${max.toLocaleString('en-US')}`;
  return null;
};

// Client-side first: a "10/s" that silently saved as "no limit" was the defect this replaces.
const problemsOf = (draft: IDestinationDraft, destination: TDestination) => {
  const problems: Partial<Record<TDraftField, string>> = {};
  if (!draft.name.trim()) problems.name = 'A destination needs a name';

  if (destination.transport === 'cli') {
    const hold = optionalWhole(draft.holdTtlSec, 1, 604_800, 'seconds');
    if (hold) problems.holdTtlSec = hold;
    const max = optionalWhole(draft.maxHoldEvents, 1, 1_000_000, 'events');
    if (max) problems.maxHoldEvents = max;
    return problems;
  }

  if (!/^https?:\/\/[^\s/]+/.test(draft.url.trim())) {
    problems.url = 'An http:// or https:// address';
  }

  const seconds = Number(draft.seconds.trim());
  if (!draft.seconds.trim() || !Number.isFinite(seconds)) {
    problems.seconds = 'A number of seconds';
  } else if (seconds < TIMEOUT_SECONDS.min || seconds > TIMEOUT_SECONDS.max) {
    problems.seconds = `Between ${TIMEOUT_SECONDS.min} and ${TIMEOUT_SECONDS.max} seconds`;
  }

  const rate = optionalWhole(draft.ratePerSec, 1, 100_000, 'requests');
  if (rate) problems.ratePerSec = rate;
  const burst = optionalWhole(draft.rateBurst, 1, 100_000, 'requests');
  if (burst) problems.rateBurst = burst;
  else if (draft.rateBurst.trim() && !draft.ratePerSec.trim()) {
    problems.rateBurst = 'A burst needs a rate to burst above';
  }

  const auth = { type: draft.authType, header: draft.authHeader };
  if (draft.authType === 'basic' && !draft.authUsername.trim()) {
    problems.authUsername = 'Basic authentication needs a username';
  }
  if (draft.authType === 'api_key' && !draft.authHeader.trim()) {
    problems.authHeader = 'Which header carries the key?';
  }
  const credentialStored = destination.authCredentialSet && draft.authCredential !== '';
  const credentialPending = Boolean(draft.authCredential);
  if (draft.authType !== 'none' && !credentialStored && !credentialPending) {
    problems.authCredential = 'Add the credential — without it nothing is sent';
  }

  const seen = new Set<string>();
  for (const row of draft.headers) {
    const key = row.key.trim().toLowerCase();
    if (!key) {
      if (row.value.trim()) problems.headers = 'A header value needs a name';
      continue;
    }
    const reason = reservedHeaderReason(key, auth);
    if (reason) problems.headers = reason;
    else if (seen.has(key)) problems.headers = `${key} is listed twice`;
    seen.add(key);
  }

  for (const name of draft.forwardHeaders) {
    const reason = name.trim() ? forwardRefusal(name, auth) : null;
    if (reason) problems.forwardHeaders = reason;
  }
  return problems;
};

// Server field → the draft field that shows it.
const SERVER_FIELD: Record<string, TDraftField> = {
  name: 'name',
  url: 'url',
  method: 'method',
  timeoutMs: 'seconds',
  rateLimitPerSec: 'ratePerSec',
  rateLimitBurst: 'rateBurst',
  headers: 'headers',
  forwardHeaders: 'forwardHeaders',
  'auth.username': 'authUsername',
  'auth.header': 'authHeader',
  'auth.prefix': 'authPrefix',
  label: 'label',
  path: 'path',
  holdTtlSec: 'holdTtlSec',
  maxHoldEvents: 'maxHoldEvents',
};

const serverProblems = (error: unknown): Partial<Record<TDraftField, string>> => {
  if (!(error instanceof ApiError)) return {};
  const out: Partial<Record<TDraftField, string>> = {};
  for (const one of error.fieldErrors) {
    const field = SERVER_FIELD[one.field];
    if (field && !out[field]) out[field] = one.message;
  }
  return out;
};

const authOf = (draft: IDestinationDraft): NonNullable<TDestinationUpdate['config']>['auth'] => {
  switch (draft.authType) {
    case 'basic':
      return { type: 'basic', username: draft.authUsername.trim() };
    case 'api_key':
      return {
        type: 'api_key',
        header: draft.authHeader.trim(),
        ...(draft.authPrefix ? { prefix: draft.authPrefix } : {}),
      };
    default:
      return { type: draft.authType };
  }
};

const bodyOf = (draft: IDestinationDraft, destination: TDestination): TDestinationUpdate => {
  const optional = (raw: string) => (raw.trim() ? Number(raw.trim()) : undefined);

  if (destination.transport === 'cli') {
    return {
      name: draft.name.trim(),
      config: {
        ...(draft.label.trim() ? { label: draft.label.trim() } : {}),
        ...(draft.path.trim() ? { path: draft.path.trim() } : {}),
        ...(optional(draft.holdTtlSec) ? { holdTtlSec: optional(draft.holdTtlSec) } : {}),
        ...(optional(draft.maxHoldEvents) ? { maxHoldEvents: optional(draft.maxHoldEvents) } : {}),
      },
    };
  }

  const headers: Record<string, string> = {};
  for (const row of draft.headers) {
    if (row.key.trim()) headers[row.key.trim()] = row.value;
  }

  // `config` is replaced whole on the server, so the untouched transport fields must be resent.
  return {
    name: draft.name.trim(),
    config: {
      ...destination.config,
      url: draft.url.trim(),
      method: draft.method,
      signOutbound: draft.signOutbound,
      headers,
      forwardHeaders: draft.forwardHeaders.map((name) => name.trim()).filter(Boolean),
      passThroughPath: draft.passThroughPath,
      passThroughQuery: draft.passThroughQuery,
      followRedirects: draft.followRedirects,
      auth: authOf(draft),
    },
    timeoutMs: toTimeoutMs(draft.seconds),
    rateLimitPerSec: whole(draft.ratePerSec),
    rateLimitBurst: draft.ratePerSec.trim() ? whole(draft.rateBurst) : null,
    ...(draft.secret === undefined ? {} : { secret: draft.secret }),
    ...(draft.authType === 'none'
      ? destination.authCredentialSet
        ? { authCredential: '' }
        : {}
      : draft.authCredential === undefined
        ? {}
        : { authCredential: draft.authCredential }),
  };
};

export interface IWaitingConfirm {
  count: number;
  urlChanged: boolean;
  secretChanged: boolean;
}

export const useDestinationSettings = (destination: TDestination) => {
  const client = useQueryClient();
  const saved = useMemo(() => draftOf(destination), [destination]);
  const [draft, setDraft] = useState<IDestinationDraft>(saved);
  const [edited, setEdited] = useState(false);
  const [confirm, setConfirm] = useState<IWaitingConfirm | null>(null);

  const current = edited ? draft : saved;

  const save = useMutation({
    mutationFn: () => updateDestination(destination.id, bodyOf(current, destination)),
    onSuccess: (updated) => {
      // The response is the saved row: seeding the cache means the form shows it at once.
      client.setQueryData(['destination', destination.id], updated);
      setDraft(draftOf(updated));
      setEdited(false);
      setConfirm(null);
      void client.invalidateQueries({ queryKey: ['destinations'] });
    },
  });

  const checking = useMutation({
    mutationFn: () => countWaiting(destination.id),
  });

  const { reset } = save;
  const change = useCallback(
    (patch: Partial<IDestinationDraft>) => {
      setDraft((previous) => ({ ...(edited ? previous : saved), ...patch }));
      setEdited(true);
      reset();
    },
    [edited, saved, reset],
  );

  const urlChanged = destination.transport === 'http' && current.url.trim() !== saved.url.trim();
  const secretChanged = Boolean(current.secret) && destination.secretSet;

  // Edits reach events already waiting on a retry: say how many before they do.
  const requestSave = async () => {
    if (!urlChanged && !secretChanged) return save.mutate();
    const count = await checking.mutateAsync().catch(() => null);
    if (count === null || count === 0) return save.mutate();
    setConfirm({ count, urlChanged, secretChanged });
  };

  const dirty = edited && JSON.stringify(current) !== JSON.stringify(saved);
  const problems = edited ? problemsOf(current, destination) : {};
  const rejected = save.isError ? serverProblems(save.error) : {};
  const problem = (field: TDraftField): string | undefined => problems[field] ?? rejected[field];
  const blocked = Object.keys(problems).length > 0;

  return {
    draft: current,
    change,
    dirty,
    named: current.name.trim().length > 0,
    canSave: dirty && !blocked,
    blocked,
    problem,
    save,
    requestSave,
    checking: checking.isPending,
    confirm,
    closeConfirm: () => setConfirm(null),
    destination,
  };
};

export type TDestinationSettings = ReturnType<typeof useDestinationSettings>;
